import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeartPulse } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Popover from '../ui/Popover';

function formatReviewDate(iso, english) {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || '';
    const [year, month, day] = iso.split('-');
    return english ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
}

/**
 * Indicatore in Gestione Clienti: visibile solo se ci sono controlli anamnesi scaduti.
 * Il clic apre un elenco a scomparsa, senza spostare il layout.
 */
export default function AnamnesisOverdueReviews({ onOpenMember, refreshKey = 0 }) {
    const { t, i18n } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const [members, setMembers] = useState([]);

    const load = useCallback(async () => {
        try {
            const api = getAuthenticatedApi();
            const res = await api.get('/gym_member_anamnesis.php?action=overdue_reviews');
            const list = Array.isArray(res.data?.members) ? res.data.members : [];
            setMembers(list);
        } catch {
            setMembers([]);
        }
    }, [getAuthenticatedApi]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    if (members.length === 0) return null;

    const englishDates = i18n.language?.startsWith('en');

    return (
        <Popover
            placement="bottom"
            title={t('gym.membersManager.anamnesisOverdue.title')}
            trigger={
                <button
                    type="button"
                    className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border border-amber-400/80 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-100 dark:border-amber-400/40 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                    aria-label={`${t('gym.membersManager.anamnesisOverdue.button')}: ${members.length}`}
                >
                    <HeartPulse size={16} className="shrink-0" aria-hidden />
                    <span className="whitespace-nowrap">{t('gym.membersManager.anamnesisOverdue.button')}</span>
                    <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-amber-600 px-1.5 text-xs font-semibold text-white">
                        {members.length}
                    </span>
                </button>
            }
            content={(close) => (
                <ul className="max-h-72 space-y-1 overflow-y-auto">
                    {members.map((member) => {
                        const shownDate = formatReviewDate(member.next_review_date, englishDates);
                        return (
                            <li key={member.user_id}>
                                <button
                                    type="button"
                                    className="flex w-full flex-col items-start rounded-lg px-2 py-2 text-left hover:bg-black/5 dark:hover:bg-white/10"
                                    aria-label={t('gym.membersManager.anamnesisOverdue.open', { name: member.name })}
                                    onClick={() => {
                                        close();
                                        onOpenMember?.(member.user_id);
                                    }}
                                >
                                    <span className="font-medium text-[#3a3a3a] dark:text-white">{member.name}</span>
                                    <span className="text-xs text-[#3a3a3a]/70 dark:text-white/60">
                                        {t('gym.membersManager.anamnesisOverdue.dueOn', { date: shownDate })}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        />
    );
}
